import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';

// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent
} from '@mui/material';

// components

// services
import { debounce } from 'lodash';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import { useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import { useGetOrganizationCandidatesQuery } from 'src/services/candidates/candidates.service';

// types
import { ListEntityType } from '../../../services/lists/lists.types';
import { useGetListByIdQuery, useAddListItemsMutation } from '../../../services/lists/lists.service';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  listId: string | null;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

interface SelectableOption {
  id: string;
  name: string;
}

export default function AddToListModal({ open, onClose, listId, onSuccess }: Props) {

  const { t } = useTranslation();

  const authContext = useAuthContext();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<SelectableOption[]>([]);

  const [addItems, { isLoading: isAddingToList }] = useAddListItemsMutation();

  const { data: currentList } = useGetListByIdQuery(listId as string,
    { skip: !listId }
  );

  const { data: contacts, isLoading: isSearchingContacts } = useGetOrganizationContactsQuery({
    page: {
      page: 1,
      per_page: 5,
    },
    search_query: searchQuery,
    org_id: authContext.organization.id,

  }, {
    skip: !currentList || currentList.entity_type !== ListEntityType.CONTACT,
  });

  const { data: candidates, isLoading: isSearchingCandidates } = useGetOrganizationCandidatesQuery({
    page: {
      page: 1,
      per_page: 5,
    },
    search_query: searchQuery

  }, {
    skip: !currentList || currentList.entity_type !== ListEntityType.CANDIDATE,
  });

  const { data: companies, isLoading: isSearchingCompanies } = useGetMyCompaniesQuery({
    params: {
      page: 1,
      per_page: 5,
      search_query: searchQuery
    }
  }, {
    skip: !currentList || currentList.entity_type !== ListEntityType.COMPANY,
  });

  const onSearch = useMemo(() => debounce((value: string) => {
    setSearchQuery(value);
  }, 500), [setSearchQuery]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setErrorMessage('');
    setSelectedItems([]);
    onClose();
  }, [onClose]);

  const handleAddToList = useCallback(async () => {
    setErrorMessage('');

    if (selectedItems.length === 0) {
      setErrorMessage(t('lists.add_to_list_modal.errors.empty_selection'));
      return;
    }

    try {
      await addItems({
        list_id: listId as string,
        data: {
          entities: selectedItems.map((item: any) => item.id),
        }
      }).unwrap();

      enqueueSnackbar(t('lists.api.add_to_list.success'), {
        variant: 'success',
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      enqueueSnackbar(t('lists.api.add_to_list.default_error'), {
        variant: 'error',
      });
    }
  }, [addItems, listId, selectedItems, t, onSuccess, handleClose]);

  const selectableOptions: SelectableOption[] = useMemo(() => {

    switch (currentList?.entity_type) {
      case ListEntityType.COMPANY:
        return companies?.results.map((company) => ({
          id: company.id,
          name: company.company_name,
        })) || [];
      case ListEntityType.CONTACT:
        return contacts?.results.map((contact) => ({
          id: contact.id,
          name: `${contact.first_name} ${contact.last_name}`,
        })) || [];
      case ListEntityType.CANDIDATE:
        return candidates?.results.map((candidate) => ({
          id: candidate.id,
          name: `${candidate.candidate_profile.first_name} ${candidate.candidate_profile.last_name}`,
        })) || [];
      default:
        return [];
    }
  }, [companies, contacts, candidates, currentList]);

  const inputLabel = useMemo(() => {
    switch (currentList?.entity_type) {
      case ListEntityType.COMPANY:
        return t('companies.profile.linked_companies.search_placeholder');
      case ListEntityType.CONTACT:
        return t('contacts.linked_contacts.search_placeholder');
      case ListEntityType.CANDIDATE:
        return t('contacts.detail.candidate_link.candidate_autocomplete.label');
      default:
        return t('common.search');
    }
  }, [currentList, t]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        {t('lists.add_to_list_modal.title')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={3}>
            <Autocomplete
              sx={{ flexGrow: 1 }}
              multiple
              options={selectableOptions}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.name}`
              }
              loading={isSearchingCandidates || isSearchingContacts || isSearchingCompanies}
              filterOptions={(options, params) => options.filter((option) => option.name)}
              freeSolo
              value={selectedItems}
              onChange={(_, value) => setSelectedItems(value as SelectableOption[])}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={inputLabel}
                />
              )}
              onInputChange={(_, value) => onSearch(value)}
              renderOption={({ key, ...rest }, option: SelectableOption) => (
                <li key={key} {...rest}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.name}`}
                    </Typography>
                  </Stack>
                </li>
              )}
            />
          </Stack>

          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          {t('common.cancel')}
        </Button>

        <LoadingButton
          type="submit"
          variant="contained"
          onClick={handleAddToList}
          disabled={isAddingToList}
          loading={isAddingToList}
        >
          {t('common.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
